'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import { studioClient } from '@/sanity/lib/client';
import { localizedMotionQuery } from '@/sanity/queries/motion';
import { IMotion } from '@/types/motion';
import { LoadingSpinner } from '@/shared';

import motionsEllipseApp from '@/assets/illustrations/motions-ellipse-app.svg';

export default function Motion() {
  const locale = useLocale();
  const [motionData, setMotionData] = useState<IMotion | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMotionData = async () => {
      try {
        const data = await studioClient.fetch<IMotion>(localizedMotionQuery(locale));
        setMotionData(data);
      } catch (error) {
        console.error('Error fetching motion data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMotionData();
  }, [locale]);

  if (loading) {
    return (
      <section className="py-16">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </section>
    );
  }

  if (!motionData) {
    return null;
  }

  // Get localized content
  const localizedTitle =
    motionData.title[locale as keyof typeof motionData.title] || motionData.title.en;
  const localizedDescription =
    motionData.description[locale as keyof typeof motionData.description] ||
    motionData.description.en;

  return (
    <section className="relative h-[682px] w-full py-16">
      {/* Background Image */}
      <div className="absolute inset-0 right-0 h-[682px]">
        <Image
          src={motionsEllipseApp}
          alt="motions-ellipse-app"
          fill
          className={`object-cover ltr:scale-x-[1] rtl:scale-x-[-1]`}
          sizes="100vw"
          priority
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50" />
      </div>

      {/* Content */}
      <div className="absolute inset-0 z-10 mx-auto flex h-[682px] max-w-7xl items-center justify-center px-4">
        <div className="flex flex-col items-center justify-center gap-12 md:flex-row">
          <h2 className="text-hero-gradient-text mb-4 text-4xl leading-tight font-bold lg:text-5xl">
            {localizedTitle}
          </h2>
          <p className="mx-auto max-w-4xl text-lg text-white/90 sm:text-xl lg:text-2xl">
            {localizedDescription}
          </p>
        </div>
      </div>
    </section>
  );
}
